import * as Config from '@/Config';

/**
 * Centralized logging system with namespace management, color configuration,
 * and multiple log levels
 *
 * @example Basic Usage
 * ```typescript
 * import { Log } from './Log';
 *
 * // Basic logging with different levels
 * Log.debug('app:init', 'Application starting up');
 * Log.info('app:api', 'API response received', { status: 200 });
 * Log.warn('app:validation', 'Missing optional field', field);
 * Log.error('app:network', 'Request failed', error);
 * ```
 *
 * @example Configuration & Namespaces
 * ```typescript
 * // Configure namespace with color and level
 * Log.config('app:ui', {
 *   color: '#FF6600',
 *   level: Log.LEVEL.INFO,
 *   prefix: '🎨'
 * });
 *
 * // Enable/disable specific namespaces
 * Log.enable(['app:ui', 'app:api']);
 * Log.disable('app:debug');
 * Log.enableAll(); // Enable all namespaces
 * ```
 *
 * @example Performance Timing
 * ```typescript
 * // Track execution time
 * Log.time('app:render', 'component-mount');
 * // ... some work ...
 * Log.timeEnd('app:render', 'component-mount');
 *
 * // Profile a function
 * const profiledFetch = Log.profile('app:api', 'fetch-users', fetchUsers);
 * const users = await profiledFetch();
 * ```
 *
 * @example Creating Child Loggers
 * ```typescript
 * // Create a namespaced logger for a specific component
 * const logger = Log.createLogger('app:component:header');
 * logger.debug('Header mounted');
 * logger.info('Navigation updated');
 * ```
 *
 * @example History & Export
 * ```typescript
 * // Enable history tracking
 * Log.enableHistory(true);
 *
 * // Export logs to JSON
 * const jsonLogs = Log.exportToJson({
 *   namespace: 'app:api',
 *   level: Log.LEVEL.ERROR
 * });
 *
 * // Get filtered history
 * const errorLogs = Log.getHistory({
 *   level: Log.LEVEL.ERROR,
 *   startTime: Date.now() - 3600000 // Last hour
 * });
 * ```
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package Engine/Core
 * @project ChalkySticks
 */
export class Log {
	// =============================================
	// Constants
	// =============================================

	/**
	 * Available log levels
	 *
	 * @type Record<string, number>
	 */
	public static readonly LEVEL: Record<string, number> = {
		DEBUG: 0,
		ERROR: 3,
		INFO: 1,
		NONE: 4,
		WARN: 2,
	} as const;

	/**
	 * Default namespace colors (for browser)
	 *
	 * @type string[]
	 */
	private static readonly DEFAULT_COLORS: string[] = [
		'#0066CC', // Blue
		'#00AA44', // Green
		'#FF6600', // Orange
		'#CC00CC', // Purple
		'#00CCCC', // Cyan
		'#CCCC00', // Yellow
	];

	/**
	 * Level prefixes for visual distinction
	 *
	 * @type Record<number, string>
	 */
	private static readonly LEVEL_PREFIXES: Record<number, string> = {
		[Log.LEVEL.DEBUG]: '🐛',
		[Log.LEVEL.ERROR]: '❌',
		[Log.LEVEL.INFO]: '・',
		[Log.LEVEL.WARN]: '⚠️',
	};

	/**
	 * Cache of enabled namespaces
	 *
	 * @type Set<string>
	 */
	private static enabledNamespaces: Set<string> = new Set();

	/**
	 * Cache of disabled namespaces (when allEnabled is false but some are disabled)
	 */
	private static disabledNamespaces: Set<string> = new Set();

	/**
	 * Configuration for each namespace
	 */
	private static namespaceConfigs: Map<string, NamespaceConfig> = new Map();

	/**
	 * Global log level
	 */
	private static globalLevel: number = Log.LEVEL.DEBUG;

	/**
	 * History of all log messages
	 */
	private static history: LogEntry[] = [];

	/**
	 * Maximum history size
	 */
	private static maxHistorySize: number = 1000;

	/**
	 * Whether to store history
	 */
	private static storeHistory: boolean = false;

	/**
	 * Performance timers
	 */
	private static timers: Map<string, number> = new Map();

	/**
	 * Counters for count() method
	 */
	private static counters: Map<string, number> = new Map();

	/**
	 * Color index for auto-assigning colors
	 */
	private static colorIndex: number = 0;

	/**
	 * Whether all namespaces are enabled
	 */
	private static allEnabled: boolean = false;

	/**
	 * Debug level logging
	 *
	 * @param string namespace
	 * @param any[] messages
	 * @return void
	 */
	public static debug(namespace: string, ...messages: any[]): void {
		this.log(this.LEVEL.DEBUG, namespace, ...messages);
	}

	/**
	 * Info level logging
	 *
	 * @param string namespace
	 * @param any[] messages
	 * @return void
	 */
	public static info(namespace: string, ...messages: any[]): void {
		this.log(this.LEVEL.INFO, namespace, ...messages);
	}

	/**
	 * Warning level logging
	 *
	 * @param string namespace
	 * @param any[] messages
	 * @return void
	 */
	public static warn(namespace: string, ...messages: any[]): void {
		this.log(this.LEVEL.WARN, namespace, ...messages);
	}

	/**
	 * Error level logging
	 *
	 * @param string namespace
	 * @param any[] messages
	 * @return void
	 */
	public static error(namespace: string, ...messages: any[]): void {
		this.log(this.LEVEL.ERROR, namespace, ...messages);
	}

	/**
	 * Configure a namespace with options
	 *
	 * @param string namespace
	 * @param NamespaceConfig options
	 * @return void
	 */
	public static config(namespace: string, options: NamespaceConfig): void {
		this.namespaceConfigs.set(namespace, {
			...this.namespaceConfigs.get(namespace),
			...options,
		});

		// Apply enabled state
		if (options.enabled !== undefined) {
			if (options.enabled) {
				this.enable(namespace);
			} else {
				this.disable(namespace);
			}
		}
	}

	/**
	 * Set global log level
	 *
	 * @param number level
	 * @return void
	 */
	public static setLevel(level: number): void {
		this.globalLevel = level;
	}

	/**
	 * Set color for a namespace
	 *
	 * @param string namespace
	 * @param string color
	 * @return void
	 */
	public static setColor(namespace: string, color: string): void {
		this.config(namespace, { color });
	}

	/**
	 * Enable specific namespaces
	 *
	 * @param string|string[] namespaces
	 * @return void
	 */
	public static enable(namespaces: string | string[]): void {
		const namespacesArray = Array.isArray(namespaces) ? namespaces : [namespaces];
		namespacesArray.forEach((ns) => this.enabledNamespaces.add(ns));
		this.saveEnabledState();
	}

	/**
	 * Disable specific namespaces
	 *
	 * @param string|string[] namespaces
	 * @return void
	 */
	public static disable(namespaces: string | string[]): void {
		const namespacesArray = Array.isArray(namespaces) ? namespaces : [namespaces];

		// If all were enabled, switch to disabled-list mode
		if (this.allEnabled) {
			namespacesArray.forEach((ns) => this.disabledNamespaces.add(ns));
		} else {
			// Remove from enabled list
			namespacesArray.forEach((ns) => this.enabledNamespaces.delete(ns));
		}

		this.saveEnabledState();
	}

	/**
	 * Enable all namespaces
	 *
	 * @return void
	 */
	public static enableAll(): void {
		this.allEnabled = true;
		this.disabledNamespaces.clear();
		this.saveEnabledState();
	}

	/**
	 * Disable all namespaces
	 *
	 * @return void
	 */
	public static disableAll(): void {
		this.allEnabled = false;
		this.enabledNamespaces.clear();
		this.disabledNamespaces.clear();
		this.saveEnabledState();
	}

	/**
	 * Check if namespace is enabled
	 *
	 * @param string namespace
	 * @return boolean
	 */
	public static isEnabled(namespace: string): boolean {
		// If all enabled, check if this specific one is disabled
		if (this.allEnabled) {
			return !this.disabledNamespaces.has(namespace);
		}

		const hasNamespace = this.enabledNamespaces.has(namespace);
		const matchesPattern = Array.from(this.enabledNamespaces).some((pattern) => namespace.startsWith(pattern));

		// Otherwise check if it's in the enabled set or matches a pattern
		return hasNamespace || matchesPattern;
	}

	/**
	 * Start a timer for performance tracking
	 *
	 * @param string namespace
	 * @param string label
	 * @return void
	 */
	public static time(namespace: string, label: string): void {
		const key = `${namespace}:${label}`;
		this.timers.set(key, performance.now());
		this.debug(namespace, `Timer started: ${label}`);
	}

	/**
	 * End a timer and log the duration
	 *
	 * @param string namespace
	 * @param string label
	 * @return void
	 */
	public static timeEnd(namespace: string, label: string): void {
		const key = `${namespace}:${label}`;
		const startTime = this.timers.get(key);

		if (startTime === undefined) {
			this.warn(namespace, `Timer not found: ${label}`);
			return;
		}

		const duration = performance.now() - startTime;
		this.timers.delete(key);

		this.debug(namespace, `${label}: ${duration.toFixed(2)}ms`);
	}

	/**
	 * Create a profiled function wrapper
	 *
	 * @param string namespace
	 * @param string label
	 * @param Function fn
	 * @return Function
	 */
	public static profile<T extends (...args: any[]) => any>(namespace: string, label: string, fn: T): T {
		return ((...args: Parameters<T>): ReturnType<T> => {
			const startTime = performance.now();

			try {
				const result = fn(...args);

				if (result instanceof Promise) {
					return result.finally(() => {
						const duration = performance.now() - startTime;
						this.debug(namespace, `${label}: ${duration.toFixed(2)}ms`);
					}) as ReturnType<T>;
				}

				const duration = performance.now() - startTime;
				this.debug(namespace, `${label}: ${duration.toFixed(2)}ms`);

				return result;
			} catch (error) {
				const duration = performance.now() - startTime;
				this.error(namespace, `${label} failed after ${duration.toFixed(2)}ms:`, error);
				throw error;
			}
		}) as T;
	}

	/**
	 * Start a collapsible group
	 *
	 * @param string namespace
	 * @param string label
	 * @return void
	 */
	public static group(namespace: string, label: string): void {
		if (typeof console.group === 'function' && this.isEnabled(namespace)) {
			const color = this.getNamespaceColor(namespace);
			console.group(`%c[${namespace}] ${label}`, `color: ${color}; font-weight: bold;`);
		}
	}

	/**
	 * End the current group
	 *
	 * @return void
	 */
	public static groupEnd(): void {
		if (typeof console.groupEnd === 'function') {
			console.groupEnd();
		}
	}

	/**
	 * Enable or disable history storage
	 *
	 * @param boolean enabled
	 * @return void
	 */
	public static enableHistory(enabled: boolean): void {
		this.storeHistory = enabled;

		if (!enabled) {
			this.clearHistory();
		}
	}

	/**
	 * Get log history
	 *
	 * @param HistoryFilter filter
	 * @return LogEntry[]
	 */
	public static getHistory(filter?: HistoryFilter): LogEntry[] {
		let results = [...this.history];

		if (!filter) {
			return results;
		}

		// Apply namespace filter
		if (filter.namespace) {
			const namespaces = Array.isArray(filter.namespace) ? filter.namespace : [filter.namespace];

			results = results.filter((entry) => namespaces.some((ns) => entry.namespace.startsWith(ns)));
		}

		// Apply level filter
		if (filter.level !== undefined) {
			results = results.filter((entry) => entry.level >= filter.level!);
		}

		// Apply time filters
		if (filter.startTime) {
			results = results.filter((entry) => entry.timestamp >= filter.startTime!);
		}

		if (filter.endTime) {
			results = results.filter((entry) => entry.timestamp <= filter.endTime!);
		}

		// Apply limit
		if (filter.limit) {
			results = results.slice(-filter.limit);
		}

		return results;
	}

	/**
	 * Clear log history
	 *
	 * @return void
	 */
	public static clearHistory(): void {
		this.history = [];
	}

	/**
	 * Export logs to JSON
	 *
	 * @param HistoryFilter filter
	 * @return string
	 */
	public static exportToJson(filter?: HistoryFilter): string {
		const entries = this.getHistory(filter);
		return JSON.stringify(entries, null, 2);
	}

	/**
	 * Export logs to CSV
	 *
	 * @param HistoryFilter filter
	 * @return string
	 */
	public static exportToCsv(filter?: HistoryFilter): string {
		const entries = this.getHistory(filter);
		const headers = ['Timestamp', 'Level', 'Namespace', 'Message'];

		const rows = entries.map((entry) => [
			new Date(entry.timestamp).toISOString(),
			this.getLevelName(entry.level),
			entry.namespace,
			JSON.stringify(entry.messages),
		]);

		return [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');
	}

	/**
	 * Create a child logger with a fixed namespace
	 *
	 * @param string namespace
	 * @return NamespacedLogger
	 */
	public static createLogger(namespace: string): NamespacedLogger {
		return {
			debug: (...messages: any[]) => this.debug(namespace, ...messages),
			error: (...messages: any[]) => this.error(namespace, ...messages),
			info: (...messages: any[]) => this.info(namespace, ...messages),
			warn: (...messages: any[]) => this.warn(namespace, ...messages),
		};
	}

	/**
	 * Assert a condition and log if false
	 *
	 * @param boolean condition
	 * @param string namespace
	 * @param string message
	 * @return void
	 */
	public static assert(condition: boolean, namespace: string, message: string): void {
		if (!condition) {
			this.error(namespace, `Assertion failed: ${message}`);

			if (typeof console.trace === 'function') {
				console.trace();
			}
		}
	}

	/**
	 * Log a table (browser only)
	 *
	 * @param string namespace
	 * @param any[] data
	 * @return void
	 */
	public static table(namespace: string, data: any[]): void {
		if (this.isEnabled(namespace) && typeof console.table === 'function') {
			this.debug(namespace, 'Table data:');
			console.table(data);
		}
	}

	/**
	 * Count occurrences of a label
	 *
	 * @param string namespace
	 * @param string label
	 * @return void
	 */
	public static count(namespace: string, label: string): void {
		const key = `${namespace}:${label}`;
		const count = (this.counters.get(key) || 0) + 1;
		this.counters.set(key, count);

		this.debug(namespace, `${label}: ${count}`);
	}

	/**
	 * Core logging implementation
	 *
	 * @param number level
	 * @param string namespace
	 * @param any[] messages
	 * @return void
	 */
	private static log(level: number, namespace: string, ...messages: any[]): void {
		// Check global level
		if (level < this.globalLevel) {
			return;
		}

		// Check namespace config level
		const config = this.namespaceConfigs.get(namespace);
		if (config?.level !== undefined && level < config.level) {
			return;
		}

		// Check if namespace is enabled
		if (!this.isEnabled(namespace)) {
			return;
		}

		// Silence all logging in production regardless of namespace config
		if (Config.Environment.IS_PRODUCTION) {
			return;
		}

		// Apply prefix/suffix from config
		let finalMessages = [...messages];

		if (config?.prefix) {
			finalMessages.unshift(config.prefix);
		}

		if (config?.suffix) {
			finalMessages.push(config.suffix);
		}

		// Add level prefix
		const levelPrefix = this.LEVEL_PREFIXES[level];

		if (levelPrefix) {
			finalMessages.unshift(levelPrefix);
		}

		// Store in history if enabled
		if (this.storeHistory) {
			this.addToHistory(level, namespace, messages);
		}

		// Get color for namespace
		const color = this.getNamespaceColor(namespace);
		const namespaceStyle = `color: ${color}; font-weight: bold;`;

		// Use appropriate console method based on level
		switch (level) {
			case this.LEVEL.ERROR:
				console.error(`%c[${namespace}]`, namespaceStyle, ...finalMessages);
				break;

			case this.LEVEL.WARN:
				console.warn(`%c[${namespace}]`, namespaceStyle, ...finalMessages);
				break;

			case this.LEVEL.INFO:
				console.info(`%c[${namespace}]`, namespaceStyle, ...finalMessages);
				break;

			default:
				console.log(`%c[${namespace}]`, namespaceStyle, ...finalMessages);
				break;
		}
	}

	/**
	 * Get color for namespace
	 *
	 * @param string namespace
	 * @return string
	 */
	private static getNamespaceColor(namespace: string): string {
		const config = this.namespaceConfigs.get(namespace);
		if (config?.color) {
			return config.color;
		}

		// Auto-assign color based on namespace hash
		let hash = 0;
		for (let i = 0; i < namespace.length; i++) {
			hash = ((hash << 5) - hash + namespace.charCodeAt(i)) & 0xffffffff;
		}

		return this.DEFAULT_COLORS[Math.abs(hash) % this.DEFAULT_COLORS.length];
	}

	/**
	 * Add entry to history
	 *
	 * @param number level
	 * @param string namespace
	 * @param any[] messages
	 * @return void
	 */
	private static addToHistory(level: number, namespace: string, messages: any[]): void {
		const entry: LogEntry = {
			level: level,
			messages: messages,
			namespace: namespace,
			timestamp: Date.now(),
		};

		// Add stack trace for errors
		if (level === this.LEVEL.ERROR) {
			entry.stack = new Error().stack;
		}

		this.history.push(entry);

		// Trim history if needed
		if (this.history.length > this.maxHistorySize) {
			this.history = this.history.slice(-this.maxHistorySize);
		}
	}

	/**
	 * Save enabled state to localStorage
	 *
	 * @return void
	 */
	private static saveEnabledState(): void {
		if (typeof window !== 'undefined') {
			if (this.allEnabled && this.disabledNamespaces.size === 0) {
				localStorage.setItem('log_enabled', '*');
			} else if (this.allEnabled && this.disabledNamespaces.size > 0) {
				// Save as "* except disabled ones"
				localStorage.setItem('log_enabled', `*,-${Array.from(this.disabledNamespaces).join(',-')}`);
			} else {
				localStorage.setItem('log_enabled', Array.from(this.enabledNamespaces).join(','));
			}
		}
	}

	/**
	 * Load enabled state from localStorage
	 *
	 * @return void
	 */
	private static loadEnabledState(): void {
		if (typeof window !== 'undefined') {
			const stored = localStorage.getItem('log_enabled');

			if (stored === '*') {
				this.allEnabled = true;
			}

			// Parse disabled list format: "*,-namespace1,-namespace2"
			else if (stored && stored.startsWith('*,')) {
				this.allEnabled = true;
				const disabled = stored
					.split(',')
					.slice(1)
					.map((ns) => ns.replace(/^-/, ''))
					.filter((ns) => ns.trim());
				this.disabledNamespaces = new Set(disabled);
			} else if (stored) {
				this.enabledNamespaces = new Set(stored.split(',').filter((ns) => ns.trim()));
			}
		}
	}

	/**
	 * Get level name from number
	 *
	 * @param number level
	 * @return string
	 */
	private static getLevelName(level: number): string {
		const names = Object.entries(this.LEVEL).find(([_, value]) => value === level);
		return names ? names[0] : 'UNKNOWN';
	}
}

// Type Definitions
// ---------------------------------------------------------------------------

interface NamespaceConfig {
	color?: string;
	enabled?: boolean;
	formatter?: (messages: any[]) => string;
	level?: number;
	prefix?: string;
	showNamespace?: boolean;
	showTimestamp?: boolean;
	suffix?: string;
}

interface LogEntry {
	level: number;
	messages: any[];
	namespace: string;
	stack?: string;
	timestamp: number;
}

interface HistoryFilter {
	endTime?: number;
	level?: number;
	limit?: number;
	namespace?: string | string[];
	startTime?: number;
}

interface NamespacedLogger {
	debug: (...messages: any[]) => void;
	error: (...messages: any[]) => void;
	info: (...messages: any[]) => void;
	warn: (...messages: any[]) => void;
}

// Export Singleton
// ---------------------------------------------------------------------------

export default Log;

Log.enableAll();
